// Data layer de "Exportar a Tiendanube": trae las imágenes generadas de TODOS los
// productos de la marca (los que vinieron de Tiendanube + los Atlas-native, sin
// tn_product_id), agrupadas por PRODUCTO, con el flag "ya exportada" (columna
// tn_exported_at). Firma la URL full al exportar.
import { supabase } from "../../lib/supabase";

const GENERADAS_BUCKET = "generadas";
const SIGNED_URL_TTL = 3600;

export interface ExportImage {
  id: string; // imagen_generada_id (UUID)
  fileName: string;
  createdAt: string;
  favorito: boolean;
  thumbUrl: string; // signed opt (para mostrar)
  alreadyExported: boolean;
}

export interface ExportProductGroup {
  productoId: string; // Atlas producto UUID (clave de agrupación)
  nombre: string; // productos.nombre
  tnProductId: number | null; // null = Atlas-native (nunca importado de TN)
  esDeTiendanube: boolean; // tnProductId != null
  precioSugerido: string | null; // precio_web ?? precio_local (prellenado en "crear nuevo")
  descripcionSugerida: string | null;
  images: ExportImage[];
}

const isImage = (f: string): boolean => /\.(png|jpe?g|webp)$/i.test(f);

/** Trae generadas de TODOS los productos de la marca (TN + Atlas-native), agrupadas por producto. */
export async function fetchExportableProducts(
  companyId: string,
  brandId: string | null,
): Promise<ExportProductGroup[]> {
  // 1) Generadas activas de la marca (con o sin tn_product_id).
  let query = supabase
    .from("imagenes_generadas")
    .select(
      "id, file_name, created_at, favorito, tn_exported_at, variante_id, " +
        "variantes!inner(nombre, descripcion, productos!inner(id, nombre, brand_id, tn_product_id, precio_web, precio_local))",
    )
    .eq("id_company", companyId)
    .eq("active", true)
    .order("created_at", { ascending: false })
    .limit(300);
  // Nota: el brand-scope se hace en JS (abajo), NO en el query. `.eq(brand_id, X)`
  // excluía los productos con brand_id NULL (Atlas-native sin marca asignada, ej.
  // los que crea el generador), que deben mostrarse igual en la marca actual.
  const { data, error } = await query;
  if (error) {
    console.error("[exportApi] query error", error);
    return [];
  }
  const rows = (data || []).filter(
    (r: any) => r.file_name && isImage(r.file_name),
  );
  if (rows.length === 0) return [];

  // 2) Firmar thumbnails (opt, con fallback a full) en paralelo.
  const signed = await Promise.all(
    rows.map(async (r: any) => {
      const optPath = `${companyId}/opt/${r.file_name}`;
      let url = "";
      const { data: s } = await supabase.storage
        .from(GENERADAS_BUCKET)
        .createSignedUrl(optPath, SIGNED_URL_TTL);
      if (s?.signedUrl) url = s.signedUrl;
      else {
        const { data: sf } = await supabase.storage
          .from(GENERADAS_BUCKET)
          .createSignedUrl(`${companyId}/full/${r.file_name}`, SIGNED_URL_TTL);
        url = sf?.signedUrl || "";
      }
      return { r, url };
    }),
  );

  // 3) Agrupar POR PRODUCTO. `variantes`/`productos` pueden venir objeto o array.
  const groups = new Map<string, ExportProductGroup>();
  for (const { r, url } of signed) {
    const v = Array.isArray(r.variantes) ? r.variantes[0] : r.variantes;
    const p = v && (Array.isArray(v.productos) ? v.productos[0] : v.productos);
    if (!p?.id) continue;
    // Brand-scope: la marca actual + los "sin marca" (brand_id null). Evita el
    // cross-brand (misma TN en 2 marcas) pero incluye los Atlas-native sin marca.
    const pBrand = (p.brand_id as string | null) ?? null;
    if (brandId && pBrand !== null && pBrand !== brandId) continue;
    const productoId = p.id as string;
    let g = groups.get(productoId);
    if (!g) {
      const tnId = p.tn_product_id != null ? Number(p.tn_product_id) : null;
      const precio = p.precio_web ?? p.precio_local ?? null;
      g = {
        productoId,
        nombre: (p.nombre as string) || (v?.nombre as string) || "Producto",
        tnProductId: tnId,
        esDeTiendanube: tnId != null,
        precioSugerido: precio != null ? String(precio) : null,
        descripcionSugerida: (v?.descripcion as string) || null,
        images: [],
      };
      groups.set(productoId, g);
    }
    g.images.push({
      id: r.id as string,
      fileName: r.file_name as string,
      createdAt: r.created_at as string,
      favorito: !!r.favorito,
      thumbUrl: url,
      alreadyExported: !!r.tn_exported_at,
    });
  }
  // Dentro de cada producto: las "ya exportadas" van al final (sort estable).
  for (const g of groups.values()) {
    g.images.sort(
      (a, b) => Number(a.alreadyExported) - Number(b.alreadyExported),
    );
  }
  // Orden de los grupos: por la imagen más reciente de cada uno.
  return [...groups.values()].sort((a, b) =>
    (b.images[0]?.createdAt || "").localeCompare(a.images[0]?.createdAt || ""),
  );
}

/** Firma la URL FULL de una generada (para mandarla como `src` a Tiendanube). */
export async function signFullUrl(
  companyId: string,
  fileName: string,
): Promise<string> {
  const { data } = await supabase.storage
    .from(GENERADAS_BUCKET)
    .createSignedUrl(`${companyId}/full/${fileName}`, SIGNED_URL_TTL);
  return data?.signedUrl || "";
}
